import React from 'react'

/**
 * Skeleton placeholders shown ONLY on first load (useAsync `loading === true`),
 * never on background refetch (`refreshing`) - so navigation / data changes for
 * already-loaded resources just swap content in place without a skeleton flash.
 *
 * Everything is built on the existing `.skeleton` class (shimmer + a
 * prefers-reduced-motion guard + modal-pause already live in global.css). The
 * table / stat-strip / card variants render the real layout wrappers so they
 * inherit existing CSS and there's no jump when data lands.
 *
 * Roots are aria-hidden (decorative). Surface a "loading" state to assistive
 * tech via aria-busy on the swapping wrapper, not on these nodes.
 */

/** One shimmer bar. */
export function SkeletonLine({
  width = '100%',
  height = 12,
  style,
}: {
  width?: number | string
  height?: number | string
  style?: React.CSSProperties
}) {
  return <div className="skeleton" aria-hidden="true" style={{ width, height, ...style }} />
}

/** N stacked lines, last one shortened to read as a paragraph. */
export function SkeletonText({
  lines = 3,
  lastLineWidth = '60%',
  gap = 8,
}: {
  lines?: number
  lastLineWidth?: number | string
  gap?: number
}) {
  return (
    <div aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', gap }}>
      {Array.from({ length: lines }, (_, i) => (
        <SkeletonLine key={i} width={i === lines - 1 ? lastLineWidth : '100%'} />
      ))}
    </div>
  )
}

// Deterministic per-column width variance so rows don't look uniform but stay
// stable across renders (no Math.random - avoids re-shuffle on every paint).
const COL_WIDTHS = ['70%', '55%', '80%', '45%', '65%', '50%', '60%', '40%']

/**
 * Renders a real `.table-wrap > table` so it inherits all table CSS. Pass the
 * real `<th>` labels so the header matches the final table exactly (header text
 * doesn't shimmer) and there's no column-count layout shift when data arrives.
 *
 * `firstColTwoLines` gives the first cell a second, shorter line so skeleton row
 * height approximates real rows that carry a name + description sub-text.
 */
export function SkeletonTable({
  headers,
  rows = 6,
  firstColTwoLines = true,
}: {
  headers: string[]
  rows?: number
  firstColTwoLines?: boolean
}) {
  return (
    <div className="table-wrap fu" aria-hidden="true">
      <table>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, r) => (
            <tr key={r}>
              {headers.map((_h, c) => (
                <td key={c}>
                  <SkeletonLine width={COL_WIDTHS[c % COL_WIDTHS.length]} />
                  {c === 0 && firstColTwoLines && (
                    <SkeletonLine width="40%" height={9} style={{ marginTop: 6 }} />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Mirrors `.stat-strip` / `.stat-cell` (a big number bar + a small label bar). */
export function SkeletonStatStrip({ cells = 4 }: { cells?: number }) {
  return (
    <div className="stat-strip fu" aria-hidden="true">
      {Array.from({ length: cells }, (_, i) => (
        <div className="stat-cell" key={i}>
          <SkeletonLine width="50%" height={24} style={{ margin: '0 auto' }} />
          <SkeletonLine width="70%" height={9} style={{ margin: '8px auto 0' }} />
        </div>
      ))}
    </div>
  )
}

/** Mirrors `.card` + `.card-header` + body of stacked lines. */
export function SkeletonCard({
  header = true,
  rows = 3,
  style,
}: {
  header?: boolean
  rows?: number
  style?: React.CSSProperties
}) {
  return (
    <div className="card fu" aria-hidden="true" style={style}>
      {header && (
        <div className="card-header">
          <SkeletonLine width="40%" height={11} />
        </div>
      )}
      <div className="card-body">
        <SkeletonText lines={rows} />
      </div>
    </div>
  )
}
